import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { bracketDetails } from "redux/action/bracketDetailsAction";
import BracketPlayoffDetails from "components/BracketPlayoffDetails";
import ArrowTail from "shared/icons/ArrowTail";
import history from "App/History";
import BracketStandingTable from "components/BracketStanding";
import { TournamentChat } from "components/TournamentChat";
import Tabs from "components/Tabs";
import CommonSchedule from "components/CommonSchedule";
import { Button, Modal } from "rsuite";
import {
  autoResolveVsModeBrackets,
  confirmBracketResult,
  updateBulkSchedule,
  updateResult,
} from "core/services/Bracket";
import { useNotification } from "shared/hooks/useNotification";
import { bracketStandingDetails } from "redux/action/bracketStandingAction";
import BracketDisputeTable from "components/BracketDispute";
import BracketAllResultsTable from "components/BracketAllResults";
import { getBracketDisputeList } from "redux/action/bracketDisputeAction";
import { useQuery } from "shared/hooks/useQuery";
import ResolveDispute from "components/ResolveDispute";
import { DisputedBracket } from "core/models/Bracket";
import { getBracketTeamsDetails } from "redux/action/bracketTeamActionAction";
import ReviewDisputeImage from "components/ReviewDisputeImage";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";
import { Reward } from "core/models/Reward";
import { addVsRewards, getRewards } from "core/services/Groups";
import ViewRewards from "components/ViewRewards";
import PrizeDistribution from "components/PrizeDistribution";
import { playoffVsRoundsList } from "redux/action/playoffVsModeAction";
import ModeratorMatchRequests from "components/ModeratorMatchRequests";

interface UrlParams {
  playoffId: string;
}

const BLANK_REWARD: Reward = {
  amount: 0,
  id: 0,
  currency: "",
  inGameName: "",
  rewardedBy: 0,
  teamName: "",
  tournamentId: "",
  userId: 0,
  userName: "",
  name: "",
};

export default function BracektPlayoffDetail() {
  const { playoffId } = useParams<UrlParams>();
  const dispatch = useTypedDispatch();
  const [openTournamentChatDrawer, setOpenTournamentChatDrawer] =
    useState(false);
  const [isSchedule, setIsSchedule] = useState(false);
  const [selectedTab, setSelectedTab] = useState("bracket-view");
  const query = useQuery();
  const [playoffOpen, setPlayoffOpen] = useState(false);
  const [resultViewOpen, setResultViewOpen] = useState(false);
  const [selectedSeed, setSelectedSeed] = useState<DisputedBracket>();
  const [selectedImage, setSelectedImage] = useState("");
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [showPrize, setShowPrize] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [showAutoResolveConfirm, setShowAutoResolveConfirm] = useState(false);
  const [isProcessingOverdueResults, setIsProcessingOverdueResults] =
    useState(false);

  function handleOpenImage(image: string) {
    setSelectedImage(image);
    setIsImageOpen(true);
  }

  function handleCloseImage() {
    setIsImageOpen(false);
    setSelectedImage("");
  }
  const handleDisputeClick = (seed: DisputedBracket) => {
    setSelectedSeed(seed);
    setPlayoffOpen(true);
  };

  const handleAllResultClick = (seed: DisputedBracket) => {
    setSelectedSeed(seed);
    setResultViewOpen(true);
  };

  useEffect(() => {
    let page = 0;
    if (query.get("page") && !isNaN(Number(query.get("page")))) {
      page = Number(query.get("page"));
    }
    dispatch(getBracketDisputeList(playoffId, { page, size: 8 }));
  }, [dispatch, query, playoffId]);

  function handlePageChange(pageNumber: number) {
    const page = pageNumber - 1;
    dispatch(getBracketDisputeList(playoffId, { page, size: 8 }));
  }

  const {
    brackets,
    tournament,
    isLoading: isBracketsLoading,
  } = useTypedSelector((state) => state.brackets);

  const playoffVsRoundsDetails = useTypedSelector(
    (state) => state.playoffVsRoundsDetails
  );
  const {
    isLoading,
    prizeConfig,
    winnerTeams,
    rewarded,
    tournamentCompleted,
    tournamentDetails,
  } = playoffVsRoundsDetails;

  useEffect(() => {
    dispatch(playoffVsRoundsList({ id: playoffId }));
  }, [dispatch, playoffId]);


  const { bracketTeams, isLoading: isTeamsLoading } = useTypedSelector(
    (state) => state.bracketTeams
  );

  const {
    disputedBrackets,
    isLoading: isBracketDisputeLoading,
    count,
    currentPage,
  } = useTypedSelector((state) => state.disputedBracekts);

  const showNotification = useNotification();

  const { standings, isLoading: isStandingLoading } = useTypedSelector(
    (state) => state.standings
  );

  useEffect(() => {
    dispatch(showLoader());
    if (playoffId) {
      dispatch(bracketDetails(playoffId));
    }
    dispatch(hideLoader());
  }, [dispatch, playoffId]);

  useEffect(() => {
    if (selectedSeed) {
      dispatch(getBracketTeamsDetails(selectedSeed.id));
    }
  }, [dispatch, selectedSeed]);

  useEffect(() => {
    dispatch(showLoader());
    if (tournament?.bracketType === "Round Robin") {
      dispatch(bracketStandingDetails(playoffId));
    }
    dispatch(hideLoader());
  }, [dispatch, tournament, playoffId]);

  useEffect(() => {
    if (
      isBracketsLoading ||
      isStandingLoading ||
      isBracketDisputeLoading ||
      isTeamsLoading
    )
      dispatch(showLoader());
    else dispatch(hideLoader());
  }, [
    isBracketsLoading,
    isStandingLoading,
    isBracketDisputeLoading,
    isTeamsLoading,
    dispatch,
  ]);

  const handleClose = () => setPlayoffOpen(false);

  function handleFullScreen() {
    history.push(`/playoff/bracket/details/${tournament?.id}/full-screen`);
  }

  function handleOpenTournamentChatDrawer() {
    setOpenTournamentChatDrawer(true);
  }

  function handleOpenScheduleDrawer() {
    setIsSchedule(true);
  }

  function closeScheduleDrawer() {
    setIsSchedule(false);
  }

  async function onSubmitSchedule(values: any) {
    dispatch(showLoader());

    const rawRound = String(values.roundSequence || "");
    const [selectedRegion, selectedRound] = rawRound.includes(":")
      ? rawRound.split(":")
      : ["", rawRound];
    const parsedRoundSequence = Number(selectedRound || values.roundSequence);
    const isDoubleElimination = String(tournament?.bracketType || "")
      .toLowerCase()
      .includes("double");

    const mapRegionToBracketType = (region: string): string | undefined => {
      if (!region) return undefined;
      if (region === "lower") return "LOSERS";
      if (region === "final") return "FINALS";
      if (region === "thirdPlace") return "THIRD PLACE";
      if (region === "roundRobin") return undefined;
      if (region === "upper") {
        return isDoubleElimination ? "WINNERS" : "SINGLE";
      }
      return undefined;
    };

    const selectedBracketType = mapRegionToBracketType(selectedRegion);

    const schedulePayload = {
      tournamentId: tournament?.id,
      matchesPerDay: values.matchesPerDay,
      roundSequence: parsedRoundSequence,
      startTime: values.startTime,
      nextMatchGapMinutes: values.nextMatchGapMinutes,
      maxResultUpdateTime: values.maxResultUpdateTime,
      checkInMinutes: values.checkInMinutes,
      gracePeriodMinutes: values.gracePeriodMinutes,
      ...(selectedBracketType ? { bracketType: selectedBracketType } : {}),
    };
    console.log("schedulePayload", schedulePayload);
    updateBulkSchedule(schedulePayload).then((res) => {
      if (res.status) {
        showNotification({
          message: "Brackets are Scheduled Successfully",
          title: "Success",
          type: "success",
        });
        closeScheduleDrawer();
      } else {
        console.log(res.message)
        showNotification({
          message: "Failed to Update Scheduled",
          title: "Error",
          type: "error",
        });
        closeScheduleDrawer();
      }
      dispatch(hideLoader());
    });
  }

  const TABS = [
    { tabTitle: "View Bracket", key: "bracket-view" },
    ...(tournament?.bracketType === "Round Robin"
      ? [{ tabTitle: "Standing", key: "standing" }]
      : []),
    { tabTitle: "Raised Dispute", key: "dispute" },
    { tabTitle: "All Results", key: "all-results" },
    { tabTitle: "Moderator requests", key: "moderator-requests" },
  ];

  const processResponse = (res: any, successMsg: string) => {
    if (res.status) {
      showNotification({
        title: "Success",
        message: successMsg,
        type: "success",
      });
      setPlayoffOpen(false);
      dispatch(bracketDetails(playoffId));
      dispatch(getBracketDisputeList(playoffId, { currentPage, size: 8 }));
    } else {
      showNotification({
        title: "Error",
        message: res.message || DEFAULT_API_ERROR,
        type: "error",
      });
    }
    dispatch(hideLoader());
  };

  const handleSubmit = (values: any) => {
    if (!selectedSeed) return;

    const payload = {
      id: selectedSeed?.id,
      results: values.results?.map((r: any) => ({
        score: r.score,
        teamId: r.teamId,
      })),
      playerResults: values.playerResults?.map((p: any) => ({
        userId: p.userId,
        stats: p.stats,
      })),
    };
    updateResult(payload).then((res) =>
      processResponse(res, "Result Updated Successfully")
    );
  };

  const handleConfirmResult = () => {
    const payload = {
      bracketId: selectedSeed?.id,
    };
    confirmBracketResult(payload).then((res) =>
      processResponse(res, "Result Confirmed Successfully")
    );
  };

  function handleClosePrize() {
    setShowPrize(false);
  }

  function handlePrizeDistributionOpen() {
    setShowPrize(true);
  }

  function handlePrizeDistribution(prizeData: any) {
    dispatch(showLoader());
    addVsRewards({ ...prizeData, tournId: playoffId }).then(
      (resp) => {
        if (resp.status && resp.data) {
          dispatch(playoffVsRoundsList({ id: playoffId }));
          showNotification({
            message: "Rewards Added Successfully",
            title: "Success",
            type: "success",
          });
        } else {
          showNotification({
            message: resp.message || DEFAULT_API_ERROR,
            title: "Error",
            type: "error",
          });
        }
        handleClosePrize();
        dispatch(hideLoader());
      }
    );
  }

  async function handleViewReward() {
    const rewardRes = await getRewards(playoffId);
    if (rewardRes.status && rewardRes.data) {
      const rewardsData = rewardRes.data.rewards.map((reward) => {
        if (reward.currency === 'XOPOINTS') {
          reward.currency = 'Arena Points'
        } else {
          reward.currency = 'Dirham'

        }
        return reward;
      });

      setRewards(rewardsData);
      setShowReward(true);
    } else {
      showNotification({
        message: rewardRes.message || DEFAULT_API_ERROR,
        title: "Error",
        type: "error",
      });
    }
  }

  function closeViewReward() {
    setTimeout(() => {
      setRewards([]);
    }, 300);
    setShowReward(false);
  }

  function handleOpenAutoResolveConfirm() {
    setShowAutoResolveConfirm(true);
  }

  function handleCloseAutoResolveConfirm() {
    if (!isProcessingOverdueResults) {
      setShowAutoResolveConfirm(false);
    }
  }

  async function handleConfirmAutoResolve() {
    if (!playoffId) return;

    setIsProcessingOverdueResults(true);
    dispatch(showLoader());

    try {
      const res = await autoResolveVsModeBrackets(playoffId);

      if (res.status) {
        const processed = res.data?.processed ?? 0;
        const skipped = res.data?.skipped ?? 0;

        showNotification({
          title: "Success",
          message: `Processed ${processed} bracket(s), skipped ${skipped}.`,
          type: "success",
        });

        setShowAutoResolveConfirm(false);
        dispatch(bracketDetails(playoffId));
        dispatch(getBracketDisputeList(playoffId, { page: currentPage, size: 8 }));
      } else {
        showNotification({
          title: "Error",
          message: res.message || DEFAULT_API_ERROR,
          type: "error",
        });
      }
    } finally {
      setIsProcessingOverdueResults(false);
      dispatch(hideLoader());
    }
  }

  return (
    <div className="play-off-detail-page">
      {tournament && brackets && (
        <BracketPlayoffDetails
          tournament={tournament}
          brackets={brackets}
          handleOpenTournamentChatDrawer={handleOpenTournamentChatDrawer}
          handleOpenScheduleDrawer={handleOpenScheduleDrawer}
          handlePrizeDistributionOpen={handlePrizeDistributionOpen}
          handleViewReward={handleViewReward}
          handleProcessOverdueResults={handleOpenAutoResolveConfirm}
          isProcessingOverdueResults={isProcessingOverdueResults}
          tournamentCompleted={tournamentCompleted}
          rewarded={rewarded}
          prizeConfig={prizeConfig}
        />
      )}

      <div className="tab-section">
        <Tabs
          className="tabs-wrapper"
          tabs={TABS}
          appearance="subtle"
          active={selectedTab}
          onSelect={setSelectedTab}
        />
      </div>

      {selectedTab === "standing" && standings && (
        <BracketStandingTable standings={standings} />
      )}

      {selectedTab === "bracket-view" && (
        <div className="bracket-view-wrapper">
          {tournament && brackets && (
            <div
              className="playoff-fullscreen-wrapper"
              onClick={handleFullScreen}
            >
              <div className="view-screen">View Bracket</div>
              <div className="arrow-wrapper">
                <ArrowTail fontSize="8px" color="#6e6af0" rotate={135} />
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === "dispute" && (
        <BracketDisputeTable
          disputes={disputedBrackets}
          count={count}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          handleDisputeClick={handleDisputeClick}
        />

      )}
      {selectedTab === "all-results" && (
        <BracketAllResultsTable
          brackets={brackets}
          handleResultClick={handleAllResultClick}
        />
      )}
      {selectedTab === "moderator-requests" && (
        <ModeratorMatchRequests tournamentId={tournament?.id} />
      )}

      {isSchedule && (
        <CommonSchedule
          onClose={closeScheduleDrawer}
          show={isSchedule}
          onSubmit={onSubmitSchedule}
          tournament={tournament}
          brackets={brackets}
        />
      )}

      <TournamentChat
        open={openTournamentChatDrawer}
        onClose={() => setOpenTournamentChatDrawer(false)}
        tournament={tournament}
      />

      {bracketTeams && selectedSeed && tournament && (
        <ResolveDispute
          show={playoffOpen}
          onClose={handleClose}
          onSubmit={handleSubmit}
          bracketTeams={bracketTeams}
          seed={selectedSeed}
          tournament={tournament}
          handleOpenImage={handleOpenImage}
          handleConfirmBracketResult={handleConfirmResult}
        />
      )}

      <ReviewDisputeImage
        show={isImageOpen}
        onClose={handleCloseImage}
        imageUrl={selectedImage}
      />
      <Modal
        onHide={() => setResultViewOpen(false)}
        show={resultViewOpen}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Result Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "grid", gap: "0.8rem", color: "#d9e8f8" }}>
            <div>
              <strong style={{ color: "#ffffff" }}>Bracket:</strong>{" "}
              {selectedSeed?.bracketId || selectedSeed?.id || "-"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Round:</strong>{" "}
              {selectedSeed?.roundName || `Round ${selectedSeed?.roundSequence || "-"}`}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Match:</strong>{" "}
              {selectedSeed?.matchNumber || selectedSeed?.matchSequence || "-"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Status:</strong> {selectedSeed?.status || "-"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Approval Status:</strong>{" "}
              {selectedSeed?.approvalStatus || "-"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Resolution Type:</strong>{" "}
              {selectedSeed?.approvalStatus === "Auto Resolved"
                ? "System Auto Resolve"
                : "Manual/Normal Flow"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Result Added/Updated By:</strong>{" "}
              {selectedSeed?.teams?.find(
                (team) => team?.id === selectedSeed?.resultUpdatedBy
              )?.name
                ? `${selectedSeed?.teams?.find(
                  (team) => team?.id === selectedSeed?.resultUpdatedBy
                )?.name} (User Team)`
                : "Admin/Moderator"}
            </div>
            <div>
              <strong style={{ color: "#ffffff" }}>Teams & Score:</strong>
              <div
                style={{
                  marginTop: "0.6rem",
                  display: "grid",
                  gap: "0.6rem",
                }}
              >
                {(selectedSeed?.teams || []).map((team) => (
                  <div
                    key={team?.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.6rem 0.8rem",
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#ffffff" }}>
                      {team?.name || "-"}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <span
                        style={{
                          minWidth: "2.4rem",
                          textAlign: "center",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "999px",
                          background: "rgba(56,156,255,0.2)",
                          color: "#9fd2ff",
                          fontWeight: 700,
                        }}
                      >
                        {team?.score !== undefined && team?.score !== null
                          ? team?.score
                          : selectedSeed?.approvalStatus === "Auto Resolved"
                            ? team?.isWinner
                              ? 1
                              : 0
                            : "-"}
                      </span>
                      <span
                        style={{
                          padding: "0.2rem 0.5rem",
                          borderRadius: "999px",
                          background: team?.checkedIn
                            ? "rgba(45,159,97,0.2)"
                            : "rgba(223,45,45,0.2)",
                          color: team?.checkedIn ? "#8ff0bb" : "#ff9f9f",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        {team?.checkedIn ? "Checked In" : "Not Checked In"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <strong style={{ color: "#ffffff" }}>Screenshots:</strong>
              {selectedSeed?.screenshots?.length ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(90px, 1fr))",
                    gap: "0.8rem",
                    marginTop: "0.6rem",
                  }}
                >
                  {selectedSeed.screenshots.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      style={{
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        padding: "0.4rem",
                        background: "#0a2236",
                        cursor: "pointer",
                      }}
                      onClick={() => handleOpenImage(image)}
                    >
                      <img
                        src={image}
                        alt={`result-screenshot-${index + 1}`}
                        style={{
                          width: "100%",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: "0.4rem" }}>No screenshot uploaded</div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>


      <Modal onHide={handleClosePrize} show={showPrize} size="md">
        <Modal.Header>
          <Modal.Title>Prize Distribution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showPrize && (
            <PrizeDistribution
              onClose={handleClosePrize}
              teams={winnerTeams}
              prizeConfig={prizeConfig}
              onAddRewards={handlePrizeDistribution}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal onHide={closeViewReward} show={showReward} size="lg">
        <Modal.Header>
          <Modal.Title>Rewards</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ViewRewards rewards={rewards} />
        </Modal.Body>
      </Modal>
      <Modal
        onHide={handleCloseAutoResolveConfirm}
        show={showAutoResolveConfirm}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Progress Bracket</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          This will auto-resolve matches whose result update window has closed
          and advance winners through the bracket. Continue?
        </Modal.Body>

        <Modal.Footer>
          <Button
            onClick={handleCloseAutoResolveConfirm}
            appearance="subtle"
            disabled={isProcessingOverdueResults}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmAutoResolve}
            appearance="primary"
            loading={isProcessingOverdueResults}
          >
            Process
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
